import React, { useEffect, useState } from "react";
import {
  Card,
  Tag,
  Space,
  Button,
  Select,
  Input,
  Row,
  Col,
  Statistic,
  Tooltip,
} from "antd";
import Table, { MAX_PAGE_SIZE } from 'components/Common/Table';
import {
  PlusOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useUrlFilters from "hooks/useUrlFilters";
import * as tmActions from "store/actions/TenantManagementActions";
import dayjs from "dayjs";

const STATUS_COLORS = {
  active: "green",
  pending: "orange",
  setting_up: "blue",
  failed: "red",
  suspended: "volcano",
  cancelled: "default",
};

// Filters and search live in the URL so the list can be shared.
const URL_FILTERS = {
  app_connection: { type: "string" },
  status: { type: "string" },
  search: { type: "string" },
};

const Tenants = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [urlFilters, setUrlFilters] = useUrlFilters(URL_FILTERS);
  const filters = {
    app_connection: urlFilters.app_connection || undefined,
    status: urlFilters.status || undefined,
    search: urlFilters.search || "",
  };
  const [searchLive, setSearchLive] = useState(filters.search);
  useEffect(() => setSearchLive(filters.search), [filters.search]);
  const [syncing, setSyncing] = useState(false);

  const fetchTenants = async () => {
    setLoading(true);
    const params = { page_size: MAX_PAGE_SIZE }; // one full page; the table pages client-side
    if (filters.app_connection) params.app_connection = filters.app_connection;
    if (filters.status) params.status = filters.status;
    if (filters.search) params.search = filters.search;
    const data = await tmActions.loadSyncedTenants(params);
    if (data) setTenants(Array.isArray(data) ? data : data.results || []);
    setLoading(false);
  };

  const fetchConnections = async () => {
    const data = await tmActions.loadAppConnections();
    if (data) setConnections(Array.isArray(data) ? data : data.results || []);
  };

  useEffect(() => {
    fetchConnections();
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [filters.app_connection, filters.status, filters.search]);

  const handleSearch = (value) => {
    if (value === filters.search) fetchTenants();
    else setUrlFilters({ search: value });
  };

  const handleSuspend = async (id) => {
    const result = await tmActions.suspendTenant(id);
    if (result) fetchTenants();
  };

  const handleActivate = async (id) => {
    const result = await tmActions.activateTenant(id);
    if (result) fetchTenants();
  };

  const handleSyncAll = async () => {
    // Trigger sync_tenants on every connected app, then reload the list.
    // sync_tenants now reaps SyncedTenant rows whose remote was hard-deleted,
    // so this is the user-facing "fix stale tenants" button.
    if (!connections.length) return;
    setSyncing(true);
    const targets = connections.filter((c) => c.is_connected);
    await Promise.all(targets.map((c) => tmActions.syncTenants(c.id)));
    await fetchTenants();
    setSyncing(false);
  };

  const stats = {
    total: tenants.length,
    active: tenants.filter((t) => t.status === "active").length,
    setting_up: tenants.filter((t) => t.status === "setting_up").length,
    suspended: tenants.filter((t) => t.status === "suspended").length,
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <a onClick={() => navigate(`/tenant-management/tenants/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: "App",
      dataIndex: "app_connection_name",
      key: "app_connection_name",
      render: (text, record) => (
        <Tag>{text}</Tag>
      ),
    },
    {
      title: "Schema",
      dataIndex: "schema_name",
      key: "schema_name",
      render: (t) => t ? <code>{t}</code> : "-",
    },
    {
      title: "Domain",
      dataIndex: "domain",
      key: "domain",
      render: (t) => t || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => <Tag color={STATUS_COLORS[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Admin Email",
      dataIndex: "admin_email",
      key: "admin_email",
      ellipsis: true,
    },
    {
      title: "Paid Until",
      dataIndex: "paid_until",
      key: "paid_until",
      render: (d) => d ? dayjs(d).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Trial",
      dataIndex: "on_trial",
      key: "on_trial",
      render: (t) => t ? <Tag color="blue">Trial</Tag> : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/tenant-management/tenants/${record.id}`)}
            />
          </Tooltip>
          {record.status === "active" && (
            <Tooltip title="Suspend">
              <Button
                size="small"
                danger
                icon={<PauseCircleOutlined />}
                onClick={() => handleSuspend(record.id)}
              />
            </Tooltip>
          )}
          {record.status === "suspended" && (
            <Tooltip title="Activate">
              <Button
                size="small"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={() => handleActivate(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: "16px" }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card><Statistic title="Total Tenants" value={stats.total} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Active" value={stats.active} valueStyle={{ color: "#3f8600" }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Setting Up" value={stats.setting_up} valueStyle={{ color: "#1890ff" }} /></Card>
        </Col>
        <Col span={6}>
          <Card><Statistic title="Suspended" value={stats.suspended} valueStyle={{ color: "#cf1322" }} /></Card>
        </Col>
      </Row>

      <Card
        title="All Tenants"
        extra={
          <Space>
            <Tooltip title="Refetches tenants from every connected app and removes local rows whose remote tenant was hard-deleted.">
              <Button
                icon={<SyncOutlined spin={syncing} />}
                onClick={handleSyncAll}
                loading={syncing}
                disabled={!connections.some((c) => c.is_connected)}
              >
                Sync
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/tenant-management/tenants/new")}
            >
              Create Tenant
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Select
            placeholder="Filter by App"
            allowClear
            style={{ width: 200 }}
            value={filters.app_connection}
            onChange={(val) => setUrlFilters({ app_connection: val })}
            options={connections.map((c) => ({ value: String(c.id), label: c.product_name }))}
          />
          <Select
            placeholder="Filter by Status"
            allowClear
            style={{ width: 160 }}
            value={filters.status}
            onChange={(val) => setUrlFilters({ status: val })}
            options={[
              { value: "active", label: "Active" },
              { value: "pending", label: "Pending" },
              { value: "setting_up", label: "Setting Up" },
              { value: "failed", label: "Failed" },
              { value: "suspended", label: "Suspended" },
              { value: "cancelled", label: "Cancelled" },
            ]}
          />
          <Input.Search
            placeholder="Search tenants..."
            style={{ width: 250 }}
            value={searchLive}
            onChange={(e) => setSearchLive(e.target.value)}
            onSearch={handleSearch}
            enterButton
          />
        </Space>
        <Table
          dataSource={tenants}
          columns={columns}
          rowKey="id"
          loading={loading}
          pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        />
      </Card>
    </div>
  );
};

export default Tenants;
